import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { useRoomTypeOptions } from "shared/lib/room-types/useRoomTypeOptions"
import { SelectOption } from "shared/types/Select"
import { WithClassName } from "shared/types/WithClassName"
import { RoomType } from "shared/types/references/RoomType"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Combobox } from "shared/ui/input/combobox/Combobox"

export interface RoomTypeInputProps extends WithClassName {
  name?: string
  loading?: boolean
  disabled?: boolean
  elevated?: boolean
}

export const RoomTypeInput = React.memo(
  ({
    name = "roomTypes",
    loading,
    disabled,
    elevated,
    className
  }: RoomTypeInputProps) => {
    const { t } = useTranslation()
    const options = useRoomTypeOptions()

    const renderValue = useCallback(
      () => (
        <span className="combobox__placeholder">{t("General.Select")}</span>
      ),
      [t]
    )

    return (
      <Combobox.Root
        name={name}
        label={t("InstallationReference.Filter.RoomTypes")}
        loading={loading}
        disabled={disabled}
        elevated={elevated}
        className={classNames("room-type-input", className)}
        items={options}
        multiple
      >
        <Combobox.Label />
        <Combobox.Control>
          <Combobox.Trigger>
            <Combobox.Value>{renderValue}</Combobox.Value>
            <Combobox.Icon />
          </Combobox.Trigger>
          <Combobox.Portal>
            <Combobox.Positioner align="start" sideOffset={8}>
              <Combobox.Popup
                aria-label={t("InstallationReference.Filter.RoomTypes")}
              >
                <Combobox.InputContainer>
                  <Combobox.Input />
                  <Icon name={IconName.SEARCH} />
                </Combobox.InputContainer>
                <Combobox.Empty />
                <Combobox.List>
                  {(option: SelectOption<RoomType>) => (
                    <Combobox.Item key={option.value} value={option}>
                      <Combobox.ItemIndicator />
                      <Combobox.ItemContent>
                        {option.label}
                      </Combobox.ItemContent>
                    </Combobox.Item>
                  )}
                </Combobox.List>
              </Combobox.Popup>
            </Combobox.Positioner>
          </Combobox.Portal>
        </Combobox.Control>
      </Combobox.Root>
    )
  }
)

RoomTypeInput.displayName = "RoomTypeInput"
